// useAuth — hook React pour login / register / logout / fetch /me.
// Source de vérité = useAuthStore (persist localStorage).

import { useCallback, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useAuthStore, type AuthTokens, type AuthUser } from '../store/authStore';

interface AuthResponse {
  user: AuthUser;
  tokens: AuthTokens;
}

interface Credentials {
  email: string;
  password: string;
}

export function useAuth() {
  const { user, tokens, setSession, clear } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const register = useCallback(
    async (credentials: Credentials) => {
      setError(null);
      setIsLoading(true);
      try {
        const result = await api.post<AuthResponse>('/api/auth/register', credentials, { authenticated: false });
        setSession(result);
        return result;
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : 'Erreur inattendue';
        setError(msg);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [setSession],
  );

  const login = useCallback(
    async (credentials: Credentials) => {
      setError(null);
      setIsLoading(true);
      try {
        const result = await api.post<AuthResponse>('/api/auth/login', credentials, { authenticated: false });
        setSession(result);
        return result;
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : 'Erreur inattendue';
        setError(msg);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [setSession],
  );

  const logout = useCallback(() => {
    clear();
  }, [clear]);

  return { user, tokens, isAuthenticated: !!user, isLoading, error, register, login, logout };
}
